/* eslint-disable react/no-danger */
import React, { useEffect, useState, useCallback, forwardRef, useRef } from "react";
import PropType from "prop-types";
import cx from "classnames";
import FocusTrap from "focus-trap-react";
import { emailCookieName } from "./constants";
import { Section } from "../../sharedComponents/Section/Section";
// eslint-disable-next-line import/no-cycle
import { Link } from "../../sharedComponents/Link";
import { RCL as useTranslation } from "../RCL";
import { Row, Cell } from "../Grid";
import { TextInput } from "../FormElements/Input/Input";
import { Dropdown } from "../FormElements/Select/Select";
// eslint-disable-next-line import/no-cycle
import { AlertConnector as Alert } from "../Alert";
// eslint-disable-next-line import/no-cycle
import { Button } from "../Button";
import * as styles from "./EmailAcquisitionForm.module.scss";
import "./Widget/Desktop/DesktopEmbed.module.scss";

const fallbackLabels = {
	title: "Subscribe now!",
	subtitle: "I agree to receive emails from Sunwing Vacations Inc. I may unsubscribe at any time.",
	email: "Email",
	gateway: "Gateway",
	submit: "Sign-up",
	unsubscribe: "You may unsubscribe at any time.",
	errorMessage: "Your email could not be confirmed. Please try again.",
	emailValidation: "Please enter a valid email",
	gatewayValidation: "Please select a gateway",
	confirmTitle: "Thank you!",
	confirmMessage: "<p>You have successfully joined our mailing list.</p>",
	confirmClose: "Close",
	marketingimage:
		"https://assets.sunwingtravelgroup.com/image/upload/f_auto,q_auto,h_372/selloffvacations-prod/ALL%20INCLUSIVE%20VACATIONS/all-inclusive-vacations-032.jpg",
};

// Needed for tracking form activity
const FORM_TIME_START = Math.floor(new Date().getTime() / 1000);
const defaultFormId = "sign-up";

const EmailAcquisitionForm = forwardRef(
	(
		{
			classNames,
			config,
			mode,
			setWidgetOpen,
			lang,
			userEmail,
			setUserEmail,
			userGateway,
			setUserGateway,
			setRegistered,
			isRegistered,
			setHasSubmitted,
			exitIntentActive,
			handleExitDismiss,
			embed,
			isExitIntentSubmitted,
			setIsExitIntentSubmitted,
			isWidgetOpen,
		},
		ref
	) => {
		const [emailIsValid, setEmailIsValid] = useState(true);
		const [emailError, setEmailError] = useState("");
		const emailRegex = /^(([^&<>()[\]\\.,;:\s@'“‘”’]+(\.[^&<>()[\]\\.,;:\s@'“‘”’]+)*)|('.+'))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

		const [gatewayError, setGatewayError] = useState("");
		const [gatewayIsValid, setGatewayIsValid] = useState(true);
		const [gatewayData, setGatewayData] = useState();

		const [isSuccess, setIsSuccess] = useState(false);
		const [isError, setIsError] = useState();
		const [isSubmitting, setIsSubmitting] = useState(false);

		const [fieldId, setFieldId] = useState();

		const formRef = useRef();
		const [formHeight, setFormHeight] = useState(null);
		const type = config?.type;

		const defaultLabels = {
			title: useTranslation({ searchKey: "subscribe-to-our-newsletter" }),
			subtitle: useTranslation({
				searchKey: "sign-up-for-access-to-exclusive-sunwing-deals-promotional-offers",
			}),
			email: useTranslation({ searchKey: "email" }),
			gateway: useTranslation({ searchKey: "gateway" }),
			submit: useTranslation({ searchKey: "sign-up" }),
			unsubscribe: useTranslation({
				searchKey: "you-may-unsubscribe-at-any-time",
			}),
			errorMessage: useTranslation({
				searchKey: "your-email-could-not-be-confirmed-please-try-again",
			}),
			emailValidation: useTranslation({
				searchKey: "please-enter-a-valid-email",
			}),
			gatewayValidation: useTranslation({
				searchKey: "please-select-a-gateway",
			}),
			confirmTitle: useTranslation({
				searchKey: "sign-up-confirmation-title",
			}),
			confirmMessage: useTranslation({
				searchKey: "you-have-successfully-signed-up-to-become-a-sunwing-member",
			}),
			confirmClose: useTranslation({
				searchKey: "sign-up-confirmation-close",
			}),
			privacyText: useTranslation({ searchKey: "privacy-policy" }),
			privacyUrl: useTranslation({ searchKey: "privacy-policy-link" }),
			contactText: useTranslation({ searchKey: "contact-us" }),
			contactUrl: useTranslation({ searchKey: "contact-us-link" }),
			marketingimage: useTranslation({
				searchKey: "sign-up-for-access-to-exclusive-sunwing-deals-image",
			}),
		};

		let labels = {
			...fallbackLabels,
			...defaultLabels,
		};

		let marketingContent = {};

		if (config?.type === "inline" || config?.type === "split") {
			if (mode === "mobile") {
				marketingContent = {
					title: config?.marketing?.inline?.[lang]?.mobileWidget?.title || labels.title,
					subtitle: config?.marketing?.inline?.[lang]?.mobileWidget?.subtitle || labels.subtitle,
				};

				if (exitIntentActive || isExitIntentSubmitted) {
					marketingContent = {
						marketingimage:
							config?.marketing?.exit?.[lang]?.mobileWidget?.image || labels.marketingimage,
						title: config?.marketing?.exit?.[lang]?.mobileWidget?.title || marketingContent.title,
						subtitle:
							config?.marketing?.exit?.[lang]?.mobileWidget?.subtitle || marketingContent.subtitle,
					};
				}
			} else if (mode === "desktop") {
				marketingContent = {
					marketingimage:
						config?.marketing?.inline?.[lang]?.desktopWidget?.image || labels.marketingimage,
					title: config?.marketing?.inline?.[lang]?.desktopWidget?.title || labels.title,
					subtitle: config?.marketing?.inline?.[lang]?.desktopWidget?.subtitle || labels.subtitle,
				};

				if ((exitIntentActive || isExitIntentSubmitted) && !embed) {
					marketingContent = {
						marketingimage:
							config?.marketing?.exit?.[lang]?.desktopWidget?.image || labels.marketingimage,
						title: config?.marketing?.exit?.[lang]?.desktopWidget?.title || marketingContent.title,
						subtitle:
							config?.marketing?.exit?.[lang]?.desktopWidget?.subtitle || marketingContent.subtitle,
					};
				}
			}
		} else if (config?.type === "default" || config?.type === "exit") {
			if (mode === "mobile") {
				marketingContent = {
					title: config?.marketing?.default?.[lang]?.mobileWidget?.title || labels.title,
					subtitle: config?.marketing?.default?.[lang]?.mobileWidget?.subtitle || labels.subtitle,
				};

				if (exitIntentActive || isExitIntentSubmitted) {
					marketingContent = {
						marketingimage:
							config?.marketing?.exit?.[lang]?.mobileWidget?.image || labels.marketingimage,
						title: config?.marketing?.exit?.[lang]?.mobileWidget?.title || marketingContent.title,
						subtitle:
							config?.marketing?.exit?.[lang]?.mobileWidget?.subtitle || marketingContent.subtitle,
					};
				}
			} else if (mode === "desktop") {
				marketingContent = {
					marketingimage:
						config?.marketing?.default?.[lang]?.desktopWidget?.image || labels.marketingimage,
					title: config?.marketing?.default?.[lang]?.desktopWidget?.title || labels.title,
					subtitle: config?.marketing?.default?.[lang]?.desktopWidget?.subtitle || labels.subtitle,
				};

				if (exitIntentActive || isExitIntentSubmitted) {
					marketingContent = {
						marketingimage:
							config?.marketing?.exit?.[lang]?.desktopWidget?.image || labels.marketingimage,
						title: config?.marketing?.exit?.[lang]?.desktopWidget?.title || marketingContent.title,
						subtitle:
							config?.marketing?.exit?.[lang]?.desktopWidget?.subtitle || marketingContent.subtitle,
					};
				}
			}
		}

		if (marketingContent?.marketingimage) {
			const needsCropping = /\/v[\d]+\//.test(marketingContent.marketingimage);

			if (needsCropping) {
				const getMatch = marketingContent.marketingimage.match(/\/(v[\d]+)\//);
				marketingContent.marketingimage = marketingContent.marketingimage.replace(
					getMatch[1],
					"f_auto,q_auto,h_372"
				);
			}
		}

		labels = {
			...labels,
			...marketingContent,
		};

		const getGatewaysArray = useCallback(async () => {
			const baseAPI = process.env.GATSBY_LPC_API_BASE_URL;

			const apiConfig = {
				method: "GET",
				headers: { "Content-Type": "application/json" },
			};

			const apiRequest = new Request(`${baseAPI}/SV/search/getGatewayforBrand/${lang}/SWG`);

			fetch(apiRequest, apiConfig)
				.then(res => {
					if (!res.ok) {
						if (res.status !== 200) {
							return res.json().then(json => {
								throw new Error(`${json.toString()} `);
							});
						}
					}
					return res.json();
				})
				.then(res => {
					const gateways = res.map(gateway => ({
						value: `${gateway.name} (${gateway.code})`,
						label: `${gateway.name} (${gateway.code})`,
					}));

					setGatewayData(gateways);

					// Store gateways for session
					sessionStorage.setItem(`${fieldId}-sign-up-gateways`, JSON.stringify(gateways));
				})
				.catch(err => {
					console.error("getGatewaysArray function error: ", err);
				});
		}, [fieldId, lang]);

		const validateForm = () => {
			let isValidInput = true;
			setIsError();

			if (userGateway === "") {
				setGatewayError(labels.gatewayValidation);
				setGatewayIsValid(false);
				isValidInput = false;
			}

			if (!emailRegex.test(userEmail)) {
				setEmailError(labels.emailValidation);
				setEmailIsValid(false);
				isValidInput = false;
			}

			setHasSubmitted(true);
			return isValidInput;
		};

		const handleChangeEmail = event => {
			setEmailError("");
			setUserEmail(event.target.value);

			if (event.target.value.length > 0) {
				if (!emailRegex.test(event.target.value)) {
					setEmailIsValid(false);
					setEmailError(labels.emailValidation);
				} else {
					setEmailIsValid(true);
				}

				setHasSubmitted(true);
			}
		};

		const handleChangeGateway = event => {
			setGatewayError("");

			if (event.target.value) {
				setGatewayIsValid(true);
				setUserGateway(event.target.value);
			} else {
				setGatewayIsValid(false);
			}

			setHasSubmitted(true);
		};

		const sendAnalytics = () => {
			window.dataLayer = window.dataLayer || [];
			window.dataLayer.push({
				event: "email-form-submitted",
			});
		};

		const handleSubmit = event => {
			event.preventDefault();

			const isValidForm = validateForm();

			// Submit to FA
			if (isValidForm && config?.service) {
				setIsSubmitting(true);

				// Create the key / value pair for payload
				const customFieldsTemp = config.customFields;
				const customFieldKeys = Object.keys(customFieldsTemp);
				const customPayload = {};

				// Handle Segment Variables
				const segmentTracking = window?.analytics?.user?.();

				customFieldKeys.forEach(key => {
					let storedValue = "";

					switch (key) {
						case "email":
							storedValue = userEmail;
							break;
						case "preferred_gateways_name":
							storedValue = userGateway ? userGateway.split("(")[0].trimEnd() : storedValue;
							break;

						case "preferred_gateways_code":
							storedValue = userGateway ? userGateway.split("(")[1].split(")")[0] : storedValue;
							break;

						case "user_id":
							storedValue = segmentTracking ? segmentTracking.id() : storedValue;
							storedValue = storedValue === null ? "" : storedValue;
							break;

						case "anonymous_id":
							storedValue = segmentTracking ? segmentTracking.anonymousId() : storedValue;
							storedValue = storedValue === null ? "" : storedValue;
							break;

						case "campaign_code":
							if (exitIntentActive) {
								storedValue = config?.marketing?.exit?.campaign_code || storedValue;
							}
							break;
						default:
							break;
					}

					customPayload[customFieldsTemp[key].id] = storedValue || customFieldsTemp[key].value;
				});

				const tempPayload = { ...config.hiddenFields, ...customPayload };
				const payload = new FormData();

				const fields = Object.keys(tempPayload);

				fields.forEach(key => {
					payload.append(key, tempPayload[key]);
				});

				payload.append("tfa_dbTimeStarted", FORM_TIME_START);

				const apiConfig = {
					method: "POST",
					headers: new Headers(),
					body: payload,
				};

				const apiRequest = new Request(config.service);

				fetch(apiRequest, apiConfig)
					.then(res => {
						if (!res.ok) {
							if (res.status !== 200) {
								throw res.status;
							}
						}
						return res.status;
					})
					.then(res => {
						if (res === 200) {
							if (mode === "desktop") {
								// Capture form height to set min height of Confirmation message
								setFormHeight(formRef.current.getBoundingClientRect().height);
							}

							setIsSuccess(true);
							// Track if the form was saved for the browser session
							sessionStorage.setItem(`${config.id}-registration`, 1);
							setRegistered("2");

							// Clear global form fields
							setUserEmail("");
							setUserGateway("");

							// Resize screen
							setHasSubmitted(true);

							if (exitIntentActive) {
								setIsExitIntentSubmitted(true);
							}

							sendAnalytics();
						}

						return res;
					})
					.catch(err => {
						console.error("form submission failed: ", err);
						setIsError(labels.errorMessage);
					})
					.finally(() => {
						setIsSubmitting(false);
					});
			}
		};

		useEffect(() => {
			// Setup hidden Form Assembly fields for payload submission
			if (config?.hiddenFields) {
				const hidden = Object.keys(config?.hiddenFields);

				if (hidden?.length) {
					const hiddenArray = [];

					hidden.forEach(key => {
						hiddenArray.push({ id: key, value: config.hiddenFields[key] });
					});
				}

				if (config?.id?.length) {
					setFieldId(config.id);
				} else {
					setFieldId(defaultFormId);
				}
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []);

		useEffect(() => {
			if (fieldId && gatewayData === undefined) {
				if (sessionStorage.getItem(`${fieldId}-sign-up-gateways`)) {
					const storedGateways = JSON.parse(sessionStorage.getItem(`${fieldId}-sign-up-gateways`));

					setGatewayData(storedGateways);
				} else {
					(async () => {
						await getGatewaysArray();
					})();
				}
			}
		}, [fieldId, gatewayData, getGatewaysArray]);

		// Detect if a submission occurred and reset success state
		useEffect(() => {
			if (isWidgetOpen && isSuccess) {
				setIsSuccess(false);
			}
		}, [isSuccess, isWidgetOpen]);

		const renderedComponent = (
			<div className={cx(styles.formSection, styles[type], classNames?.formSection)} ref={ref}>
				<Row cols="1" className={styles.formWrapper}>
					{(type !== "split" || (type === "split" && mode === "mobile" && exitIntentActive)) && (
						<Cell className={cx(styles.imageBanner, exitIntentActive ? styles.exitIntent : null)}>
							<img src={labels.marketingimage} alt="" />
							{mode === "mobile" && (
								<Button
									onClick={e => {
										e.preventDefault();
										setWidgetOpen(false);

										sessionStorage.removeItem(emailCookieName);

										if (isSuccess || isRegistered === "2") {
											setRegistered("1");
											setIsSuccess(false);
										}

										handleExitDismiss();
									}}
									noStyle
									iconPlacement="left"
									icon="close--n"
									disabled={isSubmitting}
									className={styles.closeIcon}
								/>
							)}
						</Cell>
					)}
					<Cell className={styles.formContainer}>
						{isRegistered !== "2" && (
							<form acceptCharset="UTF-8" noValidate onSubmit={e => handleSubmit(e)} ref={formRef}>
								{isError && <Alert className={styles.signUpFormAlert}>{isError}</Alert>}

								<Row
									cols="1"
									className={cx(styles.formElements, exitIntentActive ? styles.exitIntent : null)}>
									<Cell>
										{mode === "mobile" && !exitIntentActive && (
											<Button
												onClick={e => {
													e.preventDefault();
													setWidgetOpen(false);

													sessionStorage.removeItem(emailCookieName);

													if (isSuccess || isRegistered === "2") {
														setRegistered("1");
														setIsSuccess(false);
														handleExitDismiss();
													}
												}}
												noStyle
												iconPlacement="left"
												icon="close--n"
												disabled={isSubmitting}
												className={styles.closeIcon}
											/>
										)}

										<span className={styles.heading}>{labels.title}</span>
										<div
											className={styles.subtitle}
											dangerouslySetInnerHTML={{ __html: labels.subtitle }}
										/>

										<div
											className={cx(
												styles.inputWrapper,
												type !== "split" && classNames?.inputWrapperDefault
											)}>
											<TextInput
												className={cx(
													styles.emailInput,
													type !== "split" && classNames?.emailInputDefault
												)}
												label={labels.email}
												placeholder={labels.email}
												type="email"
												value={userEmail}
												onChange={event => {
													setEmailIsValid(true);
													setEmailError("");
													setUserEmail(event.target.value);
													setHasSubmitted(true);
												}}
												onBlur={e => handleChangeEmail(e)}
												error={!emailIsValid}
												errorMsg={emailError}
												labelHidden
												full
												id={`email-${fieldId}`}
												name={`email-${fieldId}`}
												disabled={isSubmitting}
											/>
											<Dropdown
												className={cx(
													styles.gatewayInput,
													type !== "split" && classNames?.gatewayInputDefault
												)}
												label={labels.gateway}
												placeholder={{ label: labels.gateway, value: "" }}
												items={gatewayData}
												onChange={e => handleChangeGateway(e)}
												error={!gatewayIsValid}
												errorMsg={gatewayError}
												value={userGateway}
												labelHidden
												full
												name={`gateway-${fieldId}`}
												id={`gateway-${fieldId}`}
												disabled={isSubmitting}
											/>
											{type === "split" && mode === "desktop" && (
												<Button
													type="submit"
													size="md"
													disabled={isSubmitting}
													className={classNames?.splitSubmit}>
													{labels.submit}
												</Button>
											)}
										</div>

										<div
											className={cx(
												styles.inputContainer,
												styles.splitContainer,
												// mode === "desktop" && embed === false && styles.modal,
												type === "split" && classNames?.splitContainer
											)}>
											{(type !== "split" || (type === "split" && mode === "mobile")) && (
												<div className={cx(styles.submitButton, classNames?.submitButtonDefault)}>
													<Button type="submit" size="md" disabled={isSubmitting}>
														{labels.submit}
													</Button>
												</div>
											)}
											<div
												className={cx(styles.legal, type !== "split" && classNames?.legalDefault)}>
												{labels?.unsubscribe && (
													<div>
														<span>{labels?.unsubscribe}</span>
													</div>
												)}
												<div>
													<Link
														to={labels.privacyUrl}
														className={cx(styles.alignRight, styles.link)}
														target="_blank">
														{labels.privacyText}
													</Link>
													<span className={cx(styles.alignRight)}>|</span>
													<Link
														to={labels.contactUrl}
														className={cx(styles.alignRight, styles.link)}
														target="_blank">
														{labels.contactText}
													</Link>
												</div>
											</div>
										</div>
									</Cell>
								</Row>
							</form>
						)}
						{((isExitIntentSubmitted && type === "exit" && (isSuccess || isRegistered === "2")) ||
							(type !== "exit" && (isRegistered === "2" || isSuccess))) && (
							<div
								className={cx(styles.confirmation)}
								style={{ minHeight: formHeight || `${formHeight}px` }}>
								<Section padding={type === "split" && "none"}>
									{mode === "desktop" && (
										<span className={styles.heading}>{labels.confirmTitle}</span>
									)}
									<div
										className={styles.message}
										dangerouslySetInnerHTML={{ __html: labels.confirmMessage }}
									/>
									{mode === "mobile" && (
										<Button
											onClick={e => {
												e.preventDefault();
												setWidgetOpen(false);

												sessionStorage.removeItem(emailCookieName);

												if (isSuccess || isRegistered === "2") {
													setRegistered("1");
													setIsSuccess(false);
													handleExitDismiss();
												}
											}}
											outline={mode === "mobile" ?? true}
											type="button"
											full={mode === "mobile" ?? true}
											size="md">
											{labels.confirmClose}
										</Button>
									)}
								</Section>
							</div>
						)}
					</Cell>
				</Row>
			</div>
		);

		if (mode === "mobile") {
			return (
				<FocusTrap
					focusTrapOptions={{
						clickOutsideDeactivates: true,
						escapeDeactivates: false,
					}}>
					{renderedComponent}
				</FocusTrap>
			);
		}
		return renderedComponent;
	}
);

EmailAcquisitionForm.propTypes = {
	config: PropType.object.isRequired,
	mode: PropType.string,
	setWidgetOpen: PropType.func.isRequired,
	lang: PropType.oneOf(["en", "fr"]).isRequired,
	userEmail: PropType.string,
	setUserEmail: PropType.func.isRequired,
	userGateway: PropType.string,
	setUserGateway: PropType.func.isRequired,
	setRegistered: PropType.func.isRequired,
	isRegistered: PropType.string,
	setHasSubmitted: PropType.func,
	classNames: PropType.object,
	exitIntentActive: PropType.bool,
	handleExitDismiss: PropType.func.isRequired,
	embed: PropType.bool,
	isExitIntentSubmitted: PropType.bool.isRequired,
	setIsExitIntentSubmitted: PropType.func.isRequired,
	isWidgetOpen: PropType.bool.isRequired,
};

EmailAcquisitionForm.defaultProps = {
	mode: undefined,
	userEmail: "",
	userGateway: "",
	isRegistered: null,
	setHasSubmitted: () => {},
	classNames: undefined,
	exitIntentActive: false,
	embed: true,
};

export default EmailAcquisitionForm;
export { EmailAcquisitionForm };
